import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui';
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ComponentPropsWithoutRef,
  type FC,
  type MouseEvent,
} from 'react';
import { portaledSurfaceVariants, type PortaledSurfaceVariant } from '../lib/portaledSurface';
import { useLoadingCallback } from '../lib/useLoadingCallback';
import { cn } from '../lib/utils';
import { usePortalContainer } from './PortalContainer';
import {
  DropdownMenuPortal,
  DropdownMenu as ShadcnDropdownMenu,
  DropdownMenuItem as ShadcnDropdownMenuItem,
} from './ui/dropdown-menu';
import { Spinner } from './ui/spinner';

type DropdownMenuProps = ComponentPropsWithoutRef<typeof ShadcnDropdownMenu>;

// Position, surface, and enter/exit motion, copied from the generated
// `ui/dropdown-menu.tsx` DropdownMenuContent. This wrapper renders the Radix
// Content itself so it can own the portal. Keep it easy to diff against
// `ui/dropdown-menu.tsx` if that file regenerates.
const radixContentBaseClasses =
  'max-h-(--radix-dropdown-menu-content-available-height) origin-(--radix-dropdown-menu-content-transform-origin) z-50 min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2';

const dropdownMenuContentVariants = portaledSurfaceVariants;

export type DropdownMenuVariant = PortaledSurfaceVariant;

type DropdownMenuContentProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
  /** Light (default) or dark surface. Pass `"dark"` for every menu opened from inside a `DarkThemeScope`. By default the menu portals to `document.body` and so escapes the scope's own `.dark`. Pass `container` to override the portal target. */
  variant?: DropdownMenuVariant;
  /**
   * Where the menu portals to. Defaults to the `PortalContainerProvider` target,
   * and then to `document.body`.
   */
  container?: ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Portal>['container'];
  /**
   * Set to `false` to render the menu in place, with no portal. Use this for a
   * menu inside a transformed grid, where a portaled menu does not appear.
   * @default true
   */
  portalled?: boolean;
};
type DropdownMenuItemProps = ComponentPropsWithoutRef<typeof ShadcnDropdownMenuItem> & {
  /** An async onClick handler; shows a loading spinner while the promise is pending */
  onClickAsync?: (e: MouseEvent<HTMLDivElement>) => Promise<unknown> | undefined;
  isLoading?: boolean;
};

const CloseDropdownMenuContext = createContext<() => void>(() => {});

export const DropdownMenu: FC<DropdownMenuProps> = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(!!defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  return (
    <CloseDropdownMenuContext.Provider value={() => handleOpenChange(false)}>
      <ShadcnDropdownMenu {...props} open={open} onOpenChange={handleOpenChange} />
    </CloseDropdownMenuContext.Provider>
  );
};

export const DropdownMenuContent: FC<DropdownMenuContentProps> = ({
  variant = 'light',
  className,
  container,
  portalled = true,
  sideOffset = 4,
  ...props
}) => {
  const providedContainer = usePortalContainer();

  const content = (
    <DropdownMenuPrimitive.Content
      data-slot="dropdown-menu-content"
      sideOffset={sideOffset}
      {...props}
      className={cn(
        radixContentBaseClasses,
        'tw label-sm',
        dropdownMenuContentVariants({ variant }),
        className,
      )}
    />
  );

  if (!portalled) return content;

  return (
    <DropdownMenuPortal container={container ?? providedContainer}>{content}</DropdownMenuPortal>
  );
};

export const DropdownMenuItem: FC<DropdownMenuItemProps> = (props) => {
  const close = useContext(CloseDropdownMenuContext);
  const {
    onClickAsync,
    onClick,
    onSelect,
    disabled,
    children,
    isLoading: isLoadingProp,
    className,
    ...rest
  } = props;
  const [isLoading, handleClick] = useLoadingCallback(onClick, onClickAsync, close);
  const resolvedIsLoading = isLoading || !!isLoadingProp;

  return (
    <ShadcnDropdownMenuItem
      {...rest}
      onClick={handleClick}
      onSelect={(e) => {
        // Keep the menu open while an async action runs so the spinner is visible
        if (onClickAsync) e.preventDefault();
        onSelect?.(e);
      }}
      disabled={resolvedIsLoading || disabled}
      className={cn(
        'tw cursor-pointer !outline-none',
        // `ui/dropdown-menu` highlights with `bg-accent`. `--accent` resolves on
        // `:root`, so a `.dark` menu inherits the light value and shows a white row.
        // The interaction tokens resolve on this element, in both themes.
        'focus:bg-interaction-neutral-background-hover focus:text-interaction-neutral-content-default',
        // Written as one arbitrary variant: this item wraps children in spans, so
        // shadcn's direct-child `*:[svg]` rule never reaches the icon. Chaining
        // `data-[…]:[&_svg]:` instead would attach the attribute to the svg.
        '[&[data-variant=destructive]_svg]:!text-destructive',
        className,
      )}
    >
      <span className="relative flex w-full items-center gap-2">
        <span
          className={cn('inline-flex w-full items-center gap-2', {
            invisible: resolvedIsLoading,
          })}
          aria-hidden={resolvedIsLoading || undefined}
        >
          {children}
        </span>
        {resolvedIsLoading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner className="shrink-0" />
          </span>
        )}
      </span>
    </ShadcnDropdownMenuItem>
  );
};

export {
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
